import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { toastActions } from './toast'
import { getRandId, readURIFromFile, generateThumbnail } from '../libs'
import { currentUserActions } from './currentUser'
import { MaxPhotoUpload, MaxUploadFileSizeForImageEditing } from '../constants'
import { CompressionPresets } from '../utils/imageCompression'
import { processImage, ImageProcessingOptions } from '../utils/imageProcessing'
import { getCsrfToken } from '../utils/csrf'
// v3追加: 画像編集API と Canvas調整ユーティリティ
import { ImageEditingService } from '../api/imageEditing'
import { applyCanvasAdjustments } from '../utils/canvasAdjustments'

// 画像編集機能専用の最大アップロード数
const MAX_PHOTO_UPLOAD_IMAGE_EDITING = 50

// Types
export interface UploadedImage {
  id: string;
  stubId: string;  // 各画像固有のstub_id（画像ごとの使用回数管理用）
  file: File;
  url?: string;  // 遅延読み込みのためオプショナルに
  name: string;
  size: number;
  type: string;
  thumbnail: string;
  metadata?: ImageMetadata;
}

export interface ProcessedImage {
  id: string;
  originalId: string;
  url: string;
  blob?: Blob; // Blob参照を追加
  appliedFeatures: string[];
  processingTime: number;
  fileSize: number;
  originalSize: number; // 元のファイルサイズを追加
}

// v3追加: AI加工処理履歴
export interface ProcessingHistory {
  id: string;
  originalImageId: string;
  processedImageUrl: string;
  processingType: 'blueSky' | 'mosaic';
  mosaicTargets?: ('face' | 'licensePlate')[];
  timestamp: number;
  thumbnailUrl: string;
}

interface ImageMetadata {
  width: number;
  height: number;
  format: string;
}

interface WatermarkSettings {
  type?: 'text' | 'image';
  text?: string;
  imageUrl?: string;
  imageData?: string; // base64エンコードされた画像データ
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  opacity: number; // 0-1
  size: number; // パーセンテージ (1-100)
  margin?: number; // エッジからの距離（ピクセル）
}

interface CompressionSettings {
  quality: number;
  maxSize?: number;
  targetSize?: '1MB' | '5MB';  // 追加：目標サイズ選択
}

interface MosaicSettings {
  areas: Array<{ x: number; y: number; width: number; height: number }>;
  intensity: number;
}

interface BackgroundSettings {
  mode: 'remove' | 'replace';
  color?: string;
  imageUrl?: string;
}

interface ContrastSettings {
  brightness: number;
  contrast: number;
  saturation: number;
}

interface WhiteBalanceSettings {
  temperature: number;
  tint: number;
}

interface ProcessingConfig {
  imageIds: string[];
  processingMethod: 'single' | 'individual';
  features: {
    watermark?: WatermarkSettings;
    compression?: CompressionSettings;
    mosaic?: MosaicSettings;
    highResolution?: { enabled: boolean };
    backgroundProcessing?: BackgroundSettings;
    contrastAdjustment?: ContrastSettings;
    whiteBalance?: WhiteBalanceSettings;
  };
}

// テンプレート関連の型定義
export interface Template {
  id: string;
  name: string;
  settings: {
    compression: CompressionSettings;
    watermark: WatermarkSettings;
    contrastAdjustment?: ContrastSettings;
    whiteBalance?: WhiteBalanceSettings;
  };
  is_default: boolean;
  created: string;
  modified: string;
}

export interface ImageEditingState {
  // 基本状態
  isLoading: boolean;

  // 画像管理
  uploadedImages: UploadedImage[];
  selectedImageIndex: number;
  selectedImage: UploadedImage | null;

  // 設定
  processingMethod: 'single' | 'individual';
  selectedSettings: string;

  // 加工機能
  features: {
    watermark: { enabled: boolean; settings?: WatermarkSettings };
    compression: { enabled: boolean; settings?: CompressionSettings };
    mosaic: { enabled: boolean; settings?: MosaicSettings };
    highResolution: { enabled: boolean; planRestricted: boolean };
    backgroundProcessing: { enabled: boolean; settings?: BackgroundSettings };
    contrastAdjustment: { enabled: boolean; settings?: ContrastSettings };
    brightnessAdjustment: { enabled: boolean; settings?: { brightness: number } };
    whiteBalance: { enabled: boolean; settings?: WhiteBalanceSettings };
  };

  // 処理状態
  isProcessing: boolean;
  processingProgress: number;
  processedImages: ProcessedImage[];

  // 結果表示状態
  resultPanelState: 'ng-examples' | 'processing' | 'results';
  currentProcessedImage: ProcessedImage | null;

  // モーダル状態
  isSettingsModalOpen: boolean;
  isConfirmModalOpen: boolean;
  isPhotoTooBigPopupShow: boolean;
  isTemplateSaveModalOpen: boolean;
  isTemplateOverwriteModalOpen: boolean;

  // テンプレート関連
  templates: Template[];
  selectedTemplate: Template | null;
  templateSaveName: string;
  templateLoading: boolean;
  templateError: string | null;

  // エラー状態
  error: string | null;

  // ===== v3追加: AI加工機能関連 =====
  // 一時画像管理
  tempImageId: number | null;

  // AI加工設定（排他的選択）
  aiProcessing: {
    selectedType: 'none' | 'blueSky' | 'mosaic';
    mosaicTargets: ('face' | 'licensePlate')[];
  };

  // AI処理中フラグ（全操作ブロック用）
  isAIProcessing: boolean;

  // 編集履歴（画像ごとに独立管理）
  editHistory: {
    [imageId: string]: ProcessingHistory[];
  };

  // 現在表示中の履歴インデックス（画像ごと）
  currentHistoryIndex: {
    [imageId: string]: number; // -1 = 最新, 0~ = 履歴インデックス
  };

  // Before/After表示切り替え（画像ごと）
  showAfter: {
    [imageId: string]: boolean; // true = After, false = Before
  };

  // AI加工使用回数管理（画像ごと）
  aiProcessingUsageByImage: {
    [imageId: string]: {
      usageCount: number;       // 現在の使用回数
      remainingCount: number;   // 残り使用可能回数
    };
  };
  aiProcessingMaxCount: number;         // 最大使用回数（定数: 5）
}

// Initial state
const initialState: ImageEditingState = {
  isLoading: false,
  uploadedImages: [],
  selectedImageIndex: -1,
  selectedImage: null,
  processingMethod: 'individual',
  selectedSettings: '通常画像加工',
  features: {
    watermark: { enabled: false, settings: {
      type: 'image',
      imageUrl: '',  // ServiceSettingsから取得
      imageData: '',  // ServiceSettingsから取得
      text: '',  // ServiceSettingsから取得
      position: 'center',
      opacity: 0.5,
      size: 20
    } },
    compression: { enabled: false },
    mosaic: { enabled: false, planRestricted: true },
    highResolution: { enabled: false, planRestricted: true },
    backgroundProcessing: { enabled: false },
    contrastAdjustment: { enabled: false },
    brightnessAdjustment: { enabled: false, settings: { brightness: 0 } },
    whiteBalance: { enabled: false },
  },
  isProcessing: false,
  processingProgress: 0,
  processedImages: [],
  resultPanelState: 'ng-examples',
  currentProcessedImage: null,
  isSettingsModalOpen: false,
  isConfirmModalOpen: false,
  isPhotoTooBigPopupShow: false,
  isTemplateSaveModalOpen: false,
  isTemplateOverwriteModalOpen: false,
  templates: [],
  selectedTemplate: null,
  templateSaveName: '',
  templateLoading: false,
  templateError: null,
  error: null,

  // ===== v3追加: AI加工機能のdefault値 =====
  tempImageId: null,
  aiProcessing: {
    selectedType: 'none',
    mosaicTargets: [],
  },
  isAIProcessing: false,
  editHistory: {},
  currentHistoryIndex: {},
  showAfter: {},
  aiProcessingUsageByImage: {},
  aiProcessingMaxCount: 30,
};

// Async thunks
export const addImages = createAsyncThunk(
  'imageEditing/addImages',
  async (files: FileList, thunkAPI: any) => {
    const { uploadedImages } = thunkAPI.getState().imageEditing
    let newImages: UploadedImage[] = [...uploadedImages]
    let skippedCount = 0
    let invalidFormatCount = 0
    let oversizedCount = 0

    try {
      // 処理方法に応じた最大枚数を取得
      const { processingMethod } = thunkAPI.getState().imageEditing;
      const maxUploadLimit = processingMethod === 'individual' ? 50 : MAX_PHOTO_UPLOAD_IMAGE_EDITING;

      // 現在のアップロード済み枚数をチェック
      if (uploadedImages.length >= maxUploadLimit) {
        const message = processingMethod === 'individual'
          ? `個別処理モードでは最大50枚までアップロード可能です。現在すでに${uploadedImages.length}枚アップロードされています。`
          : `一括処理モードでは最大50枚までアップロード可能です。現在すでに${uploadedImages.length}枚アップロードされています。`;
        thunkAPI.dispatch(toastActions.toastError(message));
        return uploadedImages;
      }

      // フォルダ選択時の枚数超過チェック
      const remainingSlots = maxUploadLimit - uploadedImages.length
      const totalFiles = files.length

      if (totalFiles > remainingSlots && remainingSlots > 0) {
        const message = processingMethod === 'individual'
          ? `フォルダ内に${totalFiles}枚の画像がありますが、個別処理モードでは最大50枚までです。最初の${remainingSlots}枚のみアップロードします。`
          : `フォルダ内に${totalFiles}枚の画像がありますが、アップロード可能枚数は残り${remainingSlots}枚です。最初の${remainingSlots}枚のみアップロードします。`;
        thunkAPI.dispatch(toastActions.toastError(message))
      }

      let processedCount = 0;
      const filesArray = Array.from(files);

      for (const file of filesArray) {
      // 処理数をカウント
      processedCount++;

      // システムファイルやドットファイルをスキップ（.DS_Storeなど）
      if (file.name.startsWith('.') || !file.type) {
        continue;
      }


      if (file.type !== 'image/png' && file.type !== 'image/jpeg' && file.type !== 'image/webp') {
        invalidFormatCount++
        continue
      }

      if (file.size >= MaxUploadFileSizeForImageEditing) {
        oversizedCount++
        continue
      }

      if (newImages.length >= maxUploadLimit) {
        skippedCount = totalFiles - processedCount + 1
        break
      }

      // サムネイルのみを生成（高速化）
      const thumbnail = await generateThumbnail(file)
      const imageId = getRandId().toString()
      const uploadedImage: UploadedImage = {
        id: imageId,
        stubId: `image_editing_${imageId}`, // 各画像固有のstub_id
        file,
        // url は遅延読み込みのため生成しない
        name: file.name,
        size: file.size,
        type: file.type,
        thumbnail, // 小さいサムネイル画像
      }

      newImages.push(uploadedImage)
    }


    // エラーサマリーを表示
    if (invalidFormatCount > 0) {
      thunkAPI.dispatch(toastActions.toastError(
        `${invalidFormatCount}個のファイルがサポートされていない形式のためスキップされました。`
      ))
    }

    if (oversizedCount > 0) {
      thunkAPI.dispatch(toastActions.toastError(
        `${oversizedCount}個のファイルがサイズ制限（10MB）を超えているためスキップされました。`
      ))
    }

    // 最初から上限に達していた場合のメッセージ
    if (uploadedImages.length >= MAX_PHOTO_UPLOAD_IMAGE_EDITING && totalFiles > 0) {
      thunkAPI.dispatch(toastActions.toastError(
        `すでに${MAX_PHOTO_UPLOAD_IMAGE_EDITING}枚アップロードされているため、新しい画像は追加できません。`
      ))
    }

    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError(
        'ファイルのアップロード中にエラーが発生しました。'
      ))
    }

    return newImages
  }
)

// テンプレート関連のAsync thunks
export const fetchTemplates = createAsyncThunk(
  'imageEditing/fetchTemplates',
  async (_, thunkAPI: any) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/image-editing-templates', {
        method: 'GET',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      })
      const data = await response.json()
      if (data.success) {
        return data.data
      }
      throw new Error(data.message || 'テンプレートの取得に失敗しました')
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('テンプレートの取得に失敗しました'))
      throw error
    }
  }
)

export const saveTemplate = createAsyncThunk(
  'imageEditing/saveTemplate',
  async ({ name, settings }: { name: string; settings: any }, thunkAPI: any) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/image-editing-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ name, settings, is_default: false }),
      })
      const data = await response.json()
      if (data.success) {
        thunkAPI.dispatch(toastActions.toastSuccess('テンプレートを保存しました'))
        return data.data
      }
      throw new Error(data.message || 'テンプレートの保存に失敗しました')
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('テンプレートの保存に失敗しました'))
      throw error
    }
  }
)

export const updateTemplate = createAsyncThunk(
  'imageEditing/updateTemplate',
  async ({ id, name, settings }: { id: string; name: string; settings: any }, thunkAPI: any) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(`/api/image-editing-templates/${id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ name, settings }),
      })
      const data = await response.json()
      if (data.success) {
        thunkAPI.dispatch(toastActions.toastSuccess('テンプレートを更新しました'))
        return data.data
      }
      throw new Error(data.message || 'テンプレートの更新に失敗しました')
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('テンプレートの更新に失敗しました'))
      throw error
    }
  }
)

export const deleteTemplate = createAsyncThunk(
  'imageEditing/deleteTemplate',
  async (id: string, thunkAPI: any) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(`/api/image-editing-templates/${id}/`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      })
      const data = await response.json()
      if (data.success) {
        thunkAPI.dispatch(toastActions.toastSuccess('テンプレートを削除しました'))
        return id
      }
      throw new Error(data.message || 'テンプレートの削除に失敗しました')
    } catch (error) {
      thunkAPI.dispatch(toastActions.toastError('テンプレートの削除に失敗しました'))
      throw error
    }
  }
)

export const processImages = createAsyncThunk(
  'imageEditing/processImages',
  async (config: ProcessingConfig, thunkAPI: any) => {
    const state = thunkAPI.getState().imageEditing
    const processedImages: ProcessedImage[] = []


    // Redux更新のデバウンス用
    let lastUpdateTime = 0;
    const updateProgressDebounced = (progress: number) => {
      const now = Date.now();
      if (now - lastUpdateTime > 100) { // 100ms間隔でのみ更新
        thunkAPI.dispatch(imageEditingActions.updateProcessingProgress(Math.round(progress)));
        lastUpdateTime = now;
      }
    };

    try {
      // 圧縮が有効かチェック
      const compressionEnabled = state.features.compression.enabled
      const compressionSettings = state.features.compression.settings || {}

      // 処理する画像を取得
      const imagesToProcess = state.uploadedImages.filter(img =>
        config.imageIds.includes(img.id)
      )

      if (imagesToProcess.length === 0) {
        return processedImages;
      }

      // 各画像を処理
      for (let i = 0; i < imagesToProcess.length; i++) {
        const image = imagesToProcess[i]
        const startTime = Date.now() // 処理開始時刻を記録
        const progress = (i / imagesToProcess.length) * 100
        updateProgressDebounced(progress)

        // 処理オプションの準備
        const processingOptions: ImageProcessingOptions = {
          // コントラスト調整設定（明るさ調整も含む）
          enableContrastAdjustment: state.features.contrastAdjustment?.enabled || state.features.brightnessAdjustment?.enabled,
          contrastOptions: (state.features.contrastAdjustment?.enabled || state.features.brightnessAdjustment?.enabled) ? {
            brightness: state.features.brightnessAdjustment?.enabled 
              ? (state.features.brightnessAdjustment.settings?.brightness || 0)
              : (state.features.contrastAdjustment?.settings?.brightness || 0),
            contrast: state.features.contrastAdjustment?.enabled 
              ? (state.features.contrastAdjustment.settings?.contrast || 0) 
              : 0,
            saturation: state.features.contrastAdjustment?.enabled 
              ? (state.features.contrastAdjustment.settings?.saturation || 0)
              : 0
          } : undefined,

          // ホワイトバランス設定
          enableWhiteBalance: state.features.whiteBalance?.enabled,
          whiteBalanceOptions: state.features.whiteBalance?.enabled ? {
            temperature: state.features.whiteBalance.settings?.temperature || 0,
            tint: state.features.whiteBalance.settings?.tint || 0,
            auto: false
          } : undefined,

          // ウォーターマーク設定
          enableWatermark: state.features.watermark.enabled,
          watermarkOptions: state.features.watermark.enabled ? {
            type: state.features.watermark.settings?.type || 'text',
            text: state.features.watermark.settings?.text,
            imageData: state.features.watermark.settings?.imageData,
            position: state.features.watermark.settings?.position || 'center',
            opacity: state.features.watermark.settings?.opacity ?? 0.7,
            size: state.features.watermark.settings?.size || 20,
            margin: state.features.watermark.settings?.margin || 20,
            color: state.features.watermark.settings?.color || '#FFFFFF'
          } : undefined,
          
          // 圧縮設定
          enableCompression: compressionEnabled,
          compressionTargetSize: compressionEnabled ? (
            compressionSettings?.targetSize === '1MB' 
              ? CompressionPresets.ONE_MB 
              : CompressionPresets.FIVE_MB
          ) : undefined,
          compressionMinSize: compressionEnabled && compressionSettings?.targetSize !== '1MB' 
            ? CompressionPresets.ONE_MB 
            : undefined,
          
          // 進捗コールバック
          onProgress: (progress) => {
            const totalProgress = (i + progress / 100) / imagesToProcess.length * 100
            updateProgressDebounced(totalProgress)
          }
        };

        // ファイルの準備
        let fileToProcess: File;
        if (image.file) {
          fileToProcess = image.file;
        } else if (image.url) {
          // URLからBlobを作成
          let blob: Blob;
          if (image.url.startsWith('data:')) {
            // data URLの場合
            const arr = image.url.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            blob = new Blob([u8arr], { type: mime });
          } else {
            // 通常のURLの場合
            try {
              const response = await fetch(image.url);
              blob = await response.blob();
            } catch (error) {
              console.error('Failed to fetch image:', error);
              continue; // 次の画像へ
            }
          }
          fileToProcess = new File([blob], image.name, { type: blob.type });
        } else {
          console.error('No file or URL available for image:', image.name);
          continue; // 次の画像へ
        }

        // 画像を処理
        let processingResult;
        try {
          processingResult = await processImage(fileToProcess, processingOptions);
        } catch (error) {
          console.error('Image processing failed:', error);
          thunkAPI.dispatch(toastActions.toastError(`画像 "${image.name}" の処理に失敗しました`));
          continue; // 次の画像へ
        }

        // 他の有効な機能を追加（すでに処理済みの機能は除外）
        const additionalFeatures: string[] = [];
        Object.entries(state.features).forEach(([featureName, feature]) => {
          if (feature.enabled &&
              featureName !== 'compression' &&
              featureName !== 'watermark' &&
              featureName !== 'contrastAdjustment' &&  // imageProcessing.tsで処理済み
              featureName !== 'brightnessAdjustment' && // imageProcessing.tsで処理済み（contrastOptionsに統合）
              featureName !== 'whiteBalance') {        // imageProcessing.tsで処理済み
            additionalFeatures.push(featureName);
          }
        });

        // 明るさ調整が有効な場合は別途追加（contrastAdjustmentとは別に表示）
        if (state.features.brightnessAdjustment?.enabled) {
          additionalFeatures.push('brightnessAdjustment');
        }

        // 処理時間を計算（ミリ秒）
        const processingTime = Date.now() - startTime;

        processedImages.push({
          id: getRandId().toString(),
          originalId: image.id,
          url: processingResult.url!,
          blob: processingResult.blob,
          appliedFeatures: [...processingResult.appliedFeatures, ...additionalFeatures],
          processingTime,
          fileSize: processingResult.fileSize,
          originalSize: processingResult.originalSize
        });
      }

      thunkAPI.dispatch(imageEditingActions.updateProcessingProgress(100))

      // 処理完了のフィードバック
      if (processedImages.length > 0) {
        // 各種処理のカウント
        const watermarkedCount = processedImages.filter(img =>
          img.appliedFeatures.includes('watermark')
        ).length;
        const compressedCount = processedImages.filter(img =>
          img.appliedFeatures.includes('compression')
        ).length;
        const autoCompressedCount = processedImages.filter(img =>
          img.appliedFeatures.includes('auto-compression-5mb')
        ).length;

        // フィードバックメッセージの構築
        const messages = [];
        
        if (watermarkedCount > 0) {
          messages.push(`ウォーターマークを適用`);
        }
        
        if (compressedCount > 0) {
          messages.push(`${compressedCount}枚の画像を圧縮しました`);
        }
        
        // パターン2（圧縮OFF + Watermark ON）の自動圧縮はメッセージに含めない
        // if (autoCompressedCount > 0) {
        //   messages.push(`${autoCompressedCount}枚を5MB以下に自動圧縮`);
        // }
        
        if (messages.length > 0) {
          thunkAPI.dispatch(toastActions.toastSuccess(
            `${processedImages.length}枚の処理が完了しました`
          ));
        } else {
          // 何も処理されなかった場合
          thunkAPI.dispatch(toastActions.toastSuccess(
            `${processedImages.length}枚の処理が完了しました`
          ));
        }
      }

      return processedImages

    } catch (error) {
      console.error('Processing failed:', error)
      throw error
    }
  }
)

// ===== v3追加: AI加工処理のAsync thunks =====

/**
 * 青空加工処理の実行
 * Canvas APIで調整適用→一時アップロード→AI処理→履歴追加→一時画像削除
 */
export const processBlueSkyImage = createAsyncThunk(
  'imageEditing/processBlueSkyImage',
  async ({ imageId }: { imageId: string }, thunkAPI: any) => {
    let tempId: number | null = null

    try {
      const state = thunkAPI.getState().imageEditing
      const { uploadedImages, features, currentHistoryIndex, editHistory } = state

      // 画像を取得
      const image = uploadedImages.find((img: any) => img.id === imageId)
      if (!image) {
        throw new Error('画像が見つかりません')
      }

      // 画像固有のstub_idを使用
      const stubId = image.stubId

      // 処理対象の画像URLを決定
      let sourceImageUrl: string
      const historyIndex = currentHistoryIndex[imageId] ?? -1

      // imageHistoryのインデックスをeditHistoryのインデックスに変換
      // imageHistory[0] = 元画像 → -1
      // imageHistory[1] = editHistory[0]
      // imageHistory[2] = editHistory[1]
      const actualHistoryIndex = historyIndex === 0 ? -1 : (historyIndex > 0 ? historyIndex - 1 : -1);

      if (actualHistoryIndex >= 0 && editHistory[imageId]?.[actualHistoryIndex]) {
        // 履歴が選択されている場合：履歴の画像を使用
        sourceImageUrl = editHistory[imageId][actualHistoryIndex].processedImageUrl
      } else {
        // 履歴が選択されていない場合：元画像を使用
        if (!image.url) {
          const imageUrl = await readURIFromFile(image.file)
          thunkAPI.dispatch(imageEditingActions.updateImageUrl({ id: imageId, url: imageUrl }))
          image.url = imageUrl
        }
        sourceImageUrl = image.url
      }

      // AI処理中フラグON（全操作ブロック）
      thunkAPI.dispatch(imageEditingActions.setIsAIProcessing(true))
      thunkAPI.dispatch(imageEditingActions.setResultPanelState('processing'))

      // Step 1: Canvas APIで調整を適用してBlobに変換
      const adjustedBlob = await applyCanvasAdjustments(sourceImageUrl, {
        contrast: features.contrastAdjustment?.settings?.contrast || 0,
        brightness: features.brightnessAdjustment?.settings?.brightness || 0,
        whiteBalance: features.whiteBalance?.settings?.temperature ? 'warm' : 'none',
      })

      // Step 2: 一時画像をS3にアップロード
      const uploadRes = await ImageEditingService.uploadTempImage(adjustedBlob)
      if (!uploadRes.data.success || !uploadRes.data.temp_id) {
        throw new Error(uploadRes.data.message || '一時画像のアップロードに失敗しました')
      }
      tempId = uploadRes.data.temp_id
      thunkAPI.dispatch(imageEditingActions.setTempImageId(tempId))

      // Step 3: AI青空加工処理を実行
      const processRes = await ImageEditingService.processBlueSky({
        stub_id: stubId,
        input_image: uploadRes.data.image_url,
      })

      if (!processRes.data.success || !processRes.data.result_image_url) {
        throw new Error(processRes.data.message || '青空加工に失敗しました')
      }

      // Step 4: 使用回数情報をRedux stateに保存（画像ごと）
      if (processRes.data.usage_count !== undefined && processRes.data.remaining_count !== undefined) {
        thunkAPI.dispatch(imageEditingActions.setAIProcessingUsage({
          imageId,
          usageCount: processRes.data.usage_count,
          remainingCount: processRes.data.remaining_count
        }))
      }

      // Step 5: 履歴エントリーを作成
      const historyEntry: ProcessingHistory = {
        id: getRandId().toString(),
        originalImageId: imageId,
        processedImageUrl: processRes.data.result_image_url,
        processingType: 'blueSky',
        timestamp: Date.now(),
        thumbnailUrl: processRes.data.result_image_url,
      }

      // 成功メッセージ
      thunkAPI.dispatch(toastActions.toastSuccess('青空加工が完了しました'))

      return { imageId, historyEntry }
    } catch (error: any) {
      thunkAPI.dispatch(
        toastActions.toastError(error.message || '青空加工に失敗しました')
      )
      throw error
    } finally {
      // Step 5: 一時画像を削除（必ず実行）
      if (tempId !== null) {
        try {
          await ImageEditingService.deleteTempImage(tempId)
          thunkAPI.dispatch(imageEditingActions.setTempImageId(null))
        } catch (deleteError) {
          console.error('一時画像の削除に失敗しました:', deleteError)
        }
      }

      // AI処理完了フラグOFF（ブロック解除）
      thunkAPI.dispatch(imageEditingActions.setIsAIProcessing(false))
      thunkAPI.dispatch(imageEditingActions.setResultPanelState('results'))
    }
  }
)

/**
 * モザイク処理の実行
 * Canvas APIで調整適用→一時アップロード→AI処理→履歴追加→一時画像削除
 */
export const processMosaicImage = createAsyncThunk(
  'imageEditing/processMosaicImage',
  async (
    { imageId, targets }: { imageId: string; targets: ('face' | 'licensePlate')[] },
    thunkAPI: any
  ) => {
    let tempId: number | null = null

    try {
      const state = thunkAPI.getState().imageEditing
      const { uploadedImages, features, currentHistoryIndex, editHistory } = state

      // 画像を取得
      const image = uploadedImages.find((img: any) => img.id === imageId)
      if (!image) {
        throw new Error('画像が見つかりません')
      }

      // 画像固有のstub_idを使用
      const stubId = image.stubId

      // 処理対象の画像URLを決定
      let sourceImageUrl: string
      const historyIndex = currentHistoryIndex[imageId] ?? -1

      // imageHistoryのインデックスをeditHistoryのインデックスに変換
      // imageHistory[0] = 元画像 → -1
      // imageHistory[1] = editHistory[0]
      // imageHistory[2] = editHistory[1]
      const actualHistoryIndex = historyIndex === 0 ? -1 : (historyIndex > 0 ? historyIndex - 1 : -1);

      if (actualHistoryIndex >= 0 && editHistory[imageId]?.[actualHistoryIndex]) {
        // 履歴が選択されている場合：履歴の画像を使用
        sourceImageUrl = editHistory[imageId][actualHistoryIndex].processedImageUrl
      } else {
        // 履歴が選択されていない場合：元画像を使用
        if (!image.url) {
          const imageUrl = await readURIFromFile(image.file)
          thunkAPI.dispatch(imageEditingActions.updateImageUrl({ id: imageId, url: imageUrl }))
          image.url = imageUrl
        }
        sourceImageUrl = image.url
      }

      // AI処理中フラグON（全操作ブロック）
      thunkAPI.dispatch(imageEditingActions.setIsAIProcessing(true))
      thunkAPI.dispatch(imageEditingActions.setResultPanelState('processing'))

      // Step 1: Canvas APIで調整を適用してBlobに変換
      const adjustedBlob = await applyCanvasAdjustments(sourceImageUrl, {
        contrast: features.contrastAdjustment?.settings?.contrast || 0,
        brightness: features.brightnessAdjustment?.settings?.brightness || 0,
        whiteBalance: features.whiteBalance?.settings?.temperature ? 'warm' : 'none',
      })

      // Step 2: 一時画像をS3にアップロード
      const uploadRes = await ImageEditingService.uploadTempImage(adjustedBlob)
      if (!uploadRes.data.success || !uploadRes.data.temp_id) {
        throw new Error(uploadRes.data.message || '一時画像のアップロードに失敗しました')
      }
      tempId = uploadRes.data.temp_id
      thunkAPI.dispatch(imageEditingActions.setTempImageId(tempId))

      // Step 3: モザイク処理を実行
      const processRes = await ImageEditingService.processMosaic({
        stub_id: stubId,
        mosaic_target_types: targets,
        input_image: uploadRes.data.image_url,
      })

      if (!processRes.data.success || !processRes.data.result_image_url) {
        throw new Error(processRes.data.message || 'モザイク処理に失敗しました')
      }

      // Step 4: 使用回数情報をRedux stateに保存（画像ごと）
      if (processRes.data.usage_count !== undefined && processRes.data.remaining_count !== undefined) {
        thunkAPI.dispatch(imageEditingActions.setAIProcessingUsage({
          imageId,
          usageCount: processRes.data.usage_count,
          remainingCount: processRes.data.remaining_count
        }))
      }

      // Step 5: 履歴エントリーを作成
      const historyEntry: ProcessingHistory = {
        id: getRandId().toString(),
        originalImageId: imageId,
        processedImageUrl: processRes.data.result_image_url,
        processingType: 'mosaic',
        mosaicTargets: targets,
        timestamp: Date.now(),
        thumbnailUrl: processRes.data.result_image_url,
      }

      // 成功メッセージ
      thunkAPI.dispatch(toastActions.toastSuccess('モザイク処理が完了しました'))

      return { imageId, historyEntry }
    } catch (error: any) {
      thunkAPI.dispatch(
        toastActions.toastError(error.message || 'モザイク処理に失敗しました')
      )
      throw error
    } finally {
      // Step 5: 一時画像を削除（必ず実行）
      if (tempId !== null) {
        try {
          await ImageEditingService.deleteTempImage(tempId)
          thunkAPI.dispatch(imageEditingActions.setTempImageId(null))
        } catch (deleteError) {
          console.error('一時画像の削除に失敗しました:', deleteError)
        }
      }

      // AI処理完了フラグOFF（ブロック解除）
      thunkAPI.dispatch(imageEditingActions.setIsAIProcessing(false))
      thunkAPI.dispatch(imageEditingActions.setResultPanelState('results'))
    }
  }
)

// Slice
const imageEditingSlice = createSlice({
  name: 'imageEditing',
  initialState,
  reducers: {
    setInitState: (state) => {
      return { ...initialState }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    removeImage: (state, action: PayloadAction<string>) => {
      state.uploadedImages = state.uploadedImages.filter(img => img.id !== action.payload)
      if (state.selectedImage?.id === action.payload) {
        state.selectedImage = null
        state.selectedImageIndex = -1
      }
    },
    selectImage: (state, action: PayloadAction<number>) => {
      state.selectedImageIndex = action.payload
      state.selectedImage = state.uploadedImages[action.payload] || null
    },
    updateImageUrl: (state, action: PayloadAction<{ id: string; url: string }>) => {
      const image = state.uploadedImages.find(img => img.id === action.payload.id)
      if (image) {
        image.url = action.payload.url
      }
      if (state.selectedImage?.id === action.payload.id) {
        state.selectedImage.url = action.payload.url
      }
    },
    reorderImages: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload
      const [removed] = state.uploadedImages.splice(fromIndex, 1)
      state.uploadedImages.splice(toIndex, 0, removed)
    },
    setProcessingMethod: (state, action: PayloadAction<'single' | 'individual'>) => {
      const method = action.payload;

      // 個別処理に切り替える際、51枚以上アップロードされている場合は切り替えない
      if (method === 'individual' && state.uploadedImages.length > 50) {
        // エラー通知はコンポーネント側で行う
        return;
      }

      // 一括処理に切り替える際、個別処理専用機能を自動的に無効化
      if (method === 'single') {
        // コントラスト調整を無効化
        if (state.features.contrastAdjustment) {
          state.features.contrastAdjustment.enabled = false;
        }
        // 明るさ調整を無効化
        if (state.features.brightnessAdjustment) {
          state.features.brightnessAdjustment.enabled = false;
        }
        // ホワイトバランスを無効化
        if (state.features.whiteBalance) {
          state.features.whiteBalance.enabled = false;
        }
      }

      state.processingMethod = method;

      // 処理方法が変更されたらテンプレート選択をリセット
      state.selectedTemplate = null;
    },
    setSelectedSettings: (state, action: PayloadAction<string>) => {
      state.selectedSettings = action.payload
    },
    toggleFeature: (state, action: PayloadAction<string>) => {
      const featureName = action.payload as keyof typeof state.features
      if (state.features[featureName]) {
        state.features[featureName].enabled = !state.features[featureName].enabled
        // 設定が変更されたらテンプレート選択をリセット
        state.selectedTemplate = null
      }
    },
    updateFeatureSettings: (state, action: PayloadAction<{ featureName: string; settings: any }>) => {
      const { featureName, settings } = action.payload
      const feature = featureName as keyof typeof state.features
      if (state.features[feature]) {
        state.features[feature].settings = settings
        // 設定が変更されたらテンプレート選択をリセット
        state.selectedTemplate = null
      }
    },
    updateContrastSettings: (state, action: PayloadAction<ContrastSettings>) => {
      state.features.contrastAdjustment.settings = action.payload;
      // 設定が変更されたらテンプレート選択をリセット
      state.selectedTemplate = null
    },
    updateWhiteBalanceSettings: (state, action: PayloadAction<WhiteBalanceSettings>) => {
      state.features.whiteBalance.settings = action.payload;
      // 設定が変更されたらテンプレート選択をリセット
      state.selectedTemplate = null
    },
    updatePlanRestrictions: (state, action: PayloadAction<{ planType: string }>) => {
      const { planType } = action.payload;
      const isAdvancedPlan = ['Basic', 'Pro', 'Elite'].includes(planType);

      // プラン制限フラグを更新
      state.features.contrastAdjustment.planRestricted = !isAdvancedPlan;
      state.features.whiteBalance.planRestricted = !isAdvancedPlan;
      // 他の高度な機能も同様に更新
      state.features.mosaic.planRestricted = !isAdvancedPlan;
      state.features.highResolution.planRestricted = !isAdvancedPlan;
      state.features.backgroundProcessing.planRestricted = !isAdvancedPlan;
    },
    updateProcessingProgress: (state, action: PayloadAction<number>) => {
      state.processingProgress = action.payload
    },
    setResultPanelState: (state, action: PayloadAction<'ng-examples' | 'processing' | 'results'>) => {
      state.resultPanelState = action.payload
    },
    setCurrentProcessedImage: (state, action: PayloadAction<ProcessedImage | null>) => {
      state.currentProcessedImage = action.payload
    },
    setSettingsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isSettingsModalOpen = action.payload
    },
    setConfirmModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmModalOpen = action.payload
    },
    setPhotoTooBigPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isPhotoTooBigPopupShow = action.payload
    },
    // テンプレート関連のアクション
    setTemplateSaveModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isTemplateSaveModalOpen = action.payload
    },
    setTemplateOverwriteModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isTemplateOverwriteModalOpen = action.payload
    },
    setTemplateSaveName: (state, action: PayloadAction<string>) => {
      state.templateSaveName = action.payload
    },
    selectTemplate: (state, action: PayloadAction<Template | null>) => {
      state.selectedTemplate = action.payload
    },
    applyTemplate: (state, action: PayloadAction<Template>) => {
      const template = action.payload

      // まず全ての機能を無効化（テンプレートに含まれない機能をOFFにするため）
      state.features.compression.enabled = false
      state.features.watermark.enabled = false
      state.features.contrastAdjustment.enabled = false
      state.features.brightnessAdjustment.enabled = false
      state.features.whiteBalance.enabled = false
      state.features.mosaic.enabled = false
      state.features.highResolution.enabled = false
      state.features.backgroundProcessing.enabled = false

      // 圧縮設定を適用
      if (template.settings.compression) {
        state.features.compression.enabled = true
        state.features.compression.settings = template.settings.compression
      }

      // ウォーターマーク設定を適用
      if (template.settings.watermark) {
        state.features.watermark.enabled = true
        state.features.watermark.settings = template.settings.watermark
      }

      // コントラスト調整設定を適用（個別処理モードのみ）
      if (template.settings.contrastAdjustment && state.processingMethod === 'individual') {
        state.features.contrastAdjustment.enabled = true
        state.features.contrastAdjustment.settings = template.settings.contrastAdjustment
      }

      // 明るさ調整設定を適用（個別処理モードのみ）
      if (template.settings.brightnessAdjustment && state.processingMethod === 'individual') {
        state.features.brightnessAdjustment.enabled = true
        state.features.brightnessAdjustment.settings = template.settings.brightnessAdjustment
      }

      // ホワイトバランス設定を適用（個別処理モードのみ）
      if (template.settings.whiteBalance && state.processingMethod === 'individual') {
        state.features.whiteBalance.enabled = true
        state.features.whiteBalance.settings = template.settings.whiteBalance
      }
    },
    clearData: (state) => {
      // 現在のウォーターマーク設定を保持（ServiceSettingsから同期された設定を維持）
      const preservedWatermarkSettings = state.features.watermark.settings;

      return {
        ...initialState,
        features: {
          ...initialState.features,
          watermark: {
            ...initialState.features.watermark,
            settings: preservedWatermarkSettings // ServiceSettingsから同期された設定を維持
          }
        }
      }
    },
    clearAllSettings: (state) => {
      // ウォーターマーク設定を保持（ServiceSettings由来）
      const preservedWatermarkSettings = state.features.watermark.settings;

      // すべての機能を無効化
      state.features.watermark.enabled = false;
      state.features.compression.enabled = false;
      state.features.contrastAdjustment.enabled = false;
      state.features.brightnessAdjustment.enabled = false;
      state.features.whiteBalance.enabled = false;

      // 調整値をリセット
      if (state.features.contrastAdjustment.settings) {
        state.features.contrastAdjustment.settings = {
          brightness: 0,
          contrast: 0,
          saturation: 0
        };
      }

      if (state.features.brightnessAdjustment.settings) {
        state.features.brightnessAdjustment.settings = {
          brightness: 0
        };
      }

      if (state.features.whiteBalance.settings) {
        state.features.whiteBalance.settings = {
          temperature: 0,
          tint: 0
        };
      }

      // ウォーターマーク設定を復元（ServiceSettings由来の設定は保持）
      state.features.watermark.settings = preservedWatermarkSettings;

      // テンプレート選択をリセット
      state.selectedTemplate = null;
    },
    clearUploadedImages: (state) => {
      // アップロード済み画像のみをクリア
      state.uploadedImages = [];
      state.selectedImage = null;
      state.selectedImageIndex = -1;
      // editHistory, processedImages, features, selectedTemplate は保持
    },

    // ===== v3追加: AI加工関連のreducers =====
    setTempImageId: (state, action: PayloadAction<number | null>) => {
      state.tempImageId = action.payload
    },
    setAIProcessingType: (state, action: PayloadAction<'none' | 'blueSky' | 'mosaic'>) => {
      state.aiProcessing.selectedType = action.payload
      if (action.payload !== 'mosaic') {
        state.aiProcessing.mosaicTargets = []
      }
    },
    toggleMosaicTarget: (state, action: PayloadAction<'face' | 'licensePlate'>) => {
      const target = action.payload
      const index = state.aiProcessing.mosaicTargets.indexOf(target)
      if (index >= 0) {
        state.aiProcessing.mosaicTargets.splice(index, 1)
      } else {
        state.aiProcessing.mosaicTargets.push(target)
      }
    },
    setIsAIProcessing: (state, action: PayloadAction<boolean>) => {
      state.isAIProcessing = action.payload
    },
    addHistoryEntry: (state, action: PayloadAction<{ imageId: string; history: ProcessingHistory }>) => {
      const { imageId, history } = action.payload
      if (!state.editHistory[imageId]) {
        state.editHistory[imageId] = []
      }
      state.editHistory[imageId].push(history)
      state.currentHistoryIndex[imageId] = -1  // 最新を表示
      state.showAfter[imageId] = true  // Afterに切り替え
    },
    selectHistory: (state, action: PayloadAction<{ imageId: string; historyIndex: number }>) => {
      const { imageId, historyIndex } = action.payload
      state.currentHistoryIndex[imageId] = historyIndex
    },
    toggleShowAfter: (state, action: PayloadAction<string>) => {
      const imageId = action.payload
      state.showAfter[imageId] = !state.showAfter[imageId]
    },
    setAIProcessingUsage: (state, action: PayloadAction<{ imageId: string; usageCount: number; remainingCount: number }>) => {
      const { imageId, usageCount, remainingCount } = action.payload
      state.aiProcessingUsageByImage[imageId] = {
        usageCount,
        remainingCount
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addImages.pending, (state) => {
        state.isLoading = true
      })
      .addCase(addImages.fulfilled, (state, action) => {
        state.isLoading = false
        state.uploadedImages = action.payload
        // 画像が追加されたら最後の画像（最新のアップロード）を選択
        if (state.uploadedImages.length > 0) {
          state.selectedImageIndex = state.uploadedImages.length - 1
          state.selectedImage = state.uploadedImages[state.uploadedImages.length - 1]
        }
      })
      .addCase(addImages.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to add images'
      })
      .addCase(processImages.pending, (state) => {
        state.isProcessing = true
        state.processingProgress = 0
        state.resultPanelState = 'processing'
      })
      .addCase(processImages.fulfilled, (state, action) => {
        state.isProcessing = false
        state.processingProgress = 100

        // 個別処理モードの場合は結果をマージ
        if (state.processingMethod === 'individual') {
          // 既存の結果をoriginalIdをキーとしたMapに変換
          const existingResults = new Map(
            state.processedImages.map(img => [img.originalId, img])
          );

          // 新しい結果を既存の結果にマージ（同じoriginalIdは上書き）
          action.payload.forEach(newImg => {
            existingResults.set(newImg.originalId, newImg);
          });

          // Mapを配列に戻す
          state.processedImages = Array.from(existingResults.values());
        } else {
          // 一括処理モードは従来通り全置き換え
          state.processedImages = action.payload
        }

        state.resultPanelState = 'results'
        if (action.payload.length > 0) {
          state.currentProcessedImage = action.payload[0]
        }
      })
      .addCase(processImages.rejected, (state, action) => {
        state.isProcessing = false
        state.processingProgress = 0
        state.resultPanelState = 'ng-examples'
        state.error = action.error.message || 'Failed to process images'
      })
      // テンプレート関連
      .addCase(fetchTemplates.pending, (state) => {
        state.templateLoading = true
        state.templateError = null
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templateLoading = false
        state.templates = action.payload
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.templateLoading = false
        state.templateError = action.error.message || 'Failed to fetch templates'
      })
      .addCase(saveTemplate.pending, (state) => {
        state.templateLoading = true
      })
      .addCase(saveTemplate.fulfilled, (state, action) => {
        state.templateLoading = false
        state.templates.push(action.payload)
        state.isTemplateSaveModalOpen = false
        state.isTemplateOverwriteModalOpen = false
        state.templateSaveName = ''
      })
      .addCase(saveTemplate.rejected, (state) => {
        state.templateLoading = false
      })
      .addCase(updateTemplate.pending, (state) => {
        state.templateLoading = true
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.templateLoading = false
        const index = state.templates.findIndex(t => t.id === action.payload.id)
        if (index !== -1) {
          state.templates[index] = action.payload
        }
        state.isTemplateOverwriteModalOpen = false
        state.templateSaveName = ''
      })
      .addCase(updateTemplate.rejected, (state) => {
        state.templateLoading = false
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(t => t.id !== action.payload)
      })
      // ===== v3追加: AI加工処理のextraReducers =====
      .addCase(processBlueSkyImage.fulfilled, (state, action) => {
        const { imageId, historyEntry } = action.payload
        // 履歴を追加
        if (!state.editHistory[imageId]) {
          state.editHistory[imageId] = []
        }
        state.editHistory[imageId].push(historyEntry)
        // 追加された履歴のインデックスを設定
        // imageHistory = [元画像(0), editHistory[0](1), editHistory[1](2), ...]
        // editHistoryの長さ = imageHistoryでの新しい項目のインデックス
        state.currentHistoryIndex[imageId] = state.editHistory[imageId].length
        state.showAfter[imageId] = true
      })
      .addCase(processBlueSkyImage.rejected, (state) => {
        // エラーハンドリングはthunk内で実施済み
        // ここでは特に追加の処理は不要
      })
      .addCase(processMosaicImage.fulfilled, (state, action) => {
        const { imageId, historyEntry } = action.payload
        // 履歴を追加
        if (!state.editHistory[imageId]) {
          state.editHistory[imageId] = []
        }
        state.editHistory[imageId].push(historyEntry)
        // 追加された履歴のインデックスを設定
        // imageHistory = [元画像(0), editHistory[0](1), editHistory[1](2), ...]
        // editHistoryの長さ = imageHistoryでの新しい項目のインデックス
        state.currentHistoryIndex[imageId] = state.editHistory[imageId].length
        state.showAfter[imageId] = true
      })
      .addCase(processMosaicImage.rejected, (state) => {
        // エラーハンドリングはthunk内で実施済み
        // ここでは特に追加の処理は不要
      })
  },
})

export const imageEditingActions = {
  ...imageEditingSlice.actions,
  addImages,
  processImages,
  fetchTemplates,
  saveTemplate,
  updateTemplate,
  deleteTemplate,
  // v3追加: AI加工処理のthunks
  processBlueSkyImage,
  processMosaicImage,
}

export default imageEditingSlice.reducer